import app from 'flarum/forum/app';
import DiscussionComposer from 'flarum/forum/components/DiscussionComposer';
import ReplyComposer from 'flarum/forum/components/ReplyComposer';
import LogInModal from 'flarum/forum/components/LogInModal';
import Stream from 'flarum/common/utils/Stream';
import type Discussion from 'flarum/common/models/Discussion';

/** Link parameters this extension reads; all are removed from the address once used. */
export const PARAMS = ['compose', 'reply', 'title', 'content', 'tags', 'ref'];

const TITLE_MAX = 80;
const CONTENT_MAX = 5000;
const REF_PATTERN = /^[a-z0-9][a-z0-9_-]{0,39}$/;

export interface QuickComposeLink {
  kind: 'discussion' | 'reply';
  title: string;
  content: string;
  tagSlugs: string[];
  ref: string;
}

const t = (key: string) => app.translator.trans(`happydigital-quick-compose.forum.${key}`);

/**
 * "?compose=1&title=…&content=…&tags=uyku&ref=liva" (new discussion, any page) or
 * "/d/12?reply=1&content=…&ref=popup" (reply, discussion page). Null when the address has neither.
 */
export function readLink(search: string = window.location.search): QuickComposeLink | null {
  const params = new URLSearchParams(search);
  const kind = params.get('compose') === '1' ? 'discussion' : params.get('reply') === '1' ? 'reply' : null;
  if (!kind) return null;

  const ref = (params.get('ref') || '').trim().toLowerCase();

  return {
    kind,
    title: (params.get('title') || '').replace(/\s+/g, ' ').trim().slice(0, TITLE_MAX),
    content: (params.get('content') || '').slice(0, CONTENT_MAX),
    tagSlugs: (params.get('tags') || '')
      .split(',')
      .map((slug) => slug.trim())
      .filter(Boolean)
      .slice(0, 5),
    ref: REF_PATTERN.test(ref) ? ref : '',
  };
}

/** Drops our parameters so a reload or a shared address does not open the box again. */
export function cleanAddress() {
  const url = new URL(window.location.href);
  PARAMS.forEach((key) => url.searchParams.delete(key));
  window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
}

/**
 * Guests sign in first: the forum reloads after logging in, the parameters are still
 * in the address, and the box opens then.
 */
export function requireUser(): boolean {
  if (app.session.user) return true;
  app.modal.show(LogInModal);
  return false;
}

function recordOpen(link: QuickComposeLink) {
  app
    .request({ method: 'POST', url: `${app.forum.attribute('apiUrl')}/quick-compose/opens`, body: { kind: link.kind, ref: link.ref }, errorHandler: () => {} })
    .catch(() => {});
}

/** The tags named in the link that the user can see, each child with its parent (as the tag picker does). */
function findTags(slugs: string[]): Promise<any[]> {
  const tagList = (app as any).tagList;
  if (!slugs.length || !(window as any).flarum?.extensions?.['flarum-tags'] || !tagList) return Promise.resolve([]);

  return tagList
    .load(['parent'])
    .then(() => {
      const found: any[] = [];
      slugs.forEach((slug) => {
        const tag = app.store.getBy('tags', 'slug', slug) as any;
        if (!tag) return;
        const parent = tag.parent && tag.parent();
        if (parent && !found.includes(parent)) found.push(parent);
        if (!found.includes(tag)) found.push(tag);
      });
      return found;
    })
    .catch(() => []);
}

/** New discussion box with title, text and tags filled in. */
export function openDiscussion(link: QuickComposeLink) {
  if (!requireUser()) return;
  cleanAddress();

  if (!app.forum.attribute<boolean>('canStartDiscussion')) {
    app.alerts.show({ type: 'error' }, t('cannot_start'));
    return;
  }

  findTags(link.tagSlugs).then((tags) => {
    app.composer.load(DiscussionComposer, { user: app.session.user, originalContent: link.content });
    app.composer.fields!.title = Stream(link.title);
    if (tags.length) (app.composer.fields as any).tags = tags;
    (app.composer.fields as any).quickComposeRef = link.ref || 'link';
    app.composer.show();
    recordOpen(link);
  });
}

/** Reply box on the discussion being viewed, with text filled in. */
export function openReply(link: QuickComposeLink, discussion: Discussion) {
  if (!requireUser()) return;
  cleanAddress();

  if (!discussion.canReply()) {
    app.alerts.show({ type: 'error' }, t('cannot_reply'));
    return;
  }

  app.composer.load(ReplyComposer, { user: app.session.user, discussion, originalContent: link.content });
  (app.composer.fields as any).quickComposeReplyRef = link.ref || 'link';
  app.composer.show();
  recordOpen(link);

  // As core's "Reply" button does: show the end of the discussion behind the box.
  const stream = app.current.get('stream');
  if (stream && !app.composer.isFullScreen()) stream.goToNumber('reply');
}
